import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { DetailCard } from '@/components/common/DetailCard';
import { createSubcontractor, updateSubcontractor } from '../api/subcontractors';
import { getApiErrorMessage } from '@/lib/api-error';
import { US_STATES } from '@/lib/constants';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import type { SubcontractorDto, CreateSubcontractorFormData, UpdateSubcontractorFormData } from '../types';

interface SubcontractorInlineFormProps {
  subcontractor: SubcontractorDto | null;
  onSave: (saved: SubcontractorDto) => void;
  onCancel?: () => void;
}

interface FormState {
  companyName: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  officePhone: string;
  cellPhone: string;
  homePhone: string;
  fax: string;
  email: string;
  defaultCommissionPercent: string | undefined;
  isActive: boolean;
}

const initialFormState: FormState = {
  companyName: '',
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  officePhone: '',
  cellPhone: '',
  homePhone: '',
  fax: '',
  email: '',
  defaultCommissionPercent: '0',
  isActive: true,
};

export function SubcontractorInlineForm({
  subcontractor,
  onSave,
  onCancel,
}: SubcontractorInlineFormProps) {
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { hasPermission } = useAuth();
  const canViewCommission = hasPermission(Permissions.CommissionsRead);

  const isEditMode = !!subcontractor?.id;

  useEffect(() => {
    if (subcontractor) {
      setFormData({
        companyName: subcontractor.companyName,
        firstName: subcontractor.firstName || '',
        lastName: subcontractor.lastName || '',
        address1: subcontractor.address1 || '',
        address2: subcontractor.address2 || '',
        city: subcontractor.city || '',
        state: subcontractor.state || '',
        zip: subcontractor.zip || '',
        officePhone: subcontractor.officePhone || '',
        cellPhone: subcontractor.cellPhone || '',
        homePhone: subcontractor.homePhone || '',
        fax: subcontractor.fax || '',
        email: subcontractor.email || '',
        defaultCommissionPercent: subcontractor.defaultCommissionPercent?.toString() ?? undefined,
        isActive: subcontractor.isActive,
      });
    } else {
      setFormData(initialFormState);
    }
    setError('');
  }, [subcontractor]);

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return;
    }

    const commissionPercent = parseFloat(formData.defaultCommissionPercent ?? '');
    if (canViewCommission && (isNaN(commissionPercent) || commissionPercent < 0 || commissionPercent > 100)) {
      setError('Commission percent must be between 0 and 100');
      return;
    }

    setIsSubmitting(true);

    try {
      let saved: SubcontractorDto;
      if (isEditMode && subcontractor) {
        const updateData: UpdateSubcontractorFormData = {
          companyName: formData.companyName,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          address1: formData.address1 || undefined,
          address2: formData.address2 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zip: formData.zip || undefined,
          officePhone: formData.officePhone || undefined,
          cellPhone: formData.cellPhone || undefined,
          homePhone: formData.homePhone || undefined,
          fax: formData.fax || undefined,
          email: formData.email || undefined,
          defaultCommissionPercent: canViewCommission ? commissionPercent : undefined,
          isActive: formData.isActive,
        };
        saved = await updateSubcontractor(subcontractor.id, updateData);
      } else {
        const createData: CreateSubcontractorFormData = {
          companyName: formData.companyName,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          address1: formData.address1 || undefined,
          address2: formData.address2 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zip: formData.zip || undefined,
          officePhone: formData.officePhone || undefined,
          cellPhone: formData.cellPhone || undefined,
          homePhone: formData.homePhone || undefined,
          fax: formData.fax || undefined,
          email: formData.email || undefined,
          defaultCommissionPercent: canViewCommission ? commissionPercent : undefined,
        };
        saved = await createSubcontractor(createData);
      }
      onSave(saved);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save subcontractor'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DetailCard title={isEditMode ? 'Subcontractor Details' : 'Add New Subcontractor'}>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
        <ErrorAlert error={error} />

        {/* Company Info Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground">Company Info</h3>
          <div className={`grid ${canViewCommission ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                placeholder="Company name"
                required
                autoFocus={!isEditMode}
              />
            </div>

            {canViewCommission && (
              <div className="space-y-2">
                <Label htmlFor="defaultCommissionPercent">Commission %</Label>
                <Input
                  id="defaultCommissionPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.defaultCommissionPercent}
                  onChange={(e) => handleChange('defaultCommissionPercent', e.target.value)}
                  placeholder="0"
                />
              </div>
            )}
          </div>
        </div>

        {/* Contact Person Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground">Contact Person</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="First name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground">Address</h3>
          <div className="space-y-2">
            <Label htmlFor="address1">Address Line 1</Label>
            <Input
              id="address1"
              value={formData.address1}
              onChange={(e) => handleChange('address1', e.target.value)}
              placeholder="Street address"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address2">Address Line 2</Label>
            <Input
              id="address2"
              value={formData.address2}
              onChange={(e) => handleChange('address2', e.target.value)}
              placeholder="Apt, suite, etc."
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="City"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select
                id="state"
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
              >
                <option value="">Select</option>
                {US_STATES.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.code}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="zip">ZIP</Label>
              <Input
                id="zip"
                value={formData.zip}
                onChange={(e) => handleChange('zip', e.target.value)}
                placeholder="ZIP"
              />
            </div>
          </div>
        </div>

        {/* Contact Info Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground">Contact Info</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="officePhone">Office Phone</Label>
              <Input
                id="officePhone"
                type="tel"
                value={formData.officePhone}
                onChange={(e) => handleChange('officePhone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cellPhone">Cell Phone</Label>
              <Input
                id="cellPhone"
                type="tel"
                value={formData.cellPhone}
                onChange={(e) => handleChange('cellPhone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="homePhone">Home Phone</Label>
              <Input
                id="homePhone"
                type="tel"
                value={formData.homePhone}
                onChange={(e) => handleChange('homePhone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fax">Fax</Label>
              <Input
                id="fax"
                type="tel"
                value={formData.fax}
                onChange={(e) => handleChange('fax', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="email@company.com"
              />
            </div>
          </div>
        </div>

        {/* Status Section */}
        {isEditMode && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground">Status</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
              <Label htmlFor="isActive" className="text-sm font-normal">
                Active
              </Label>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Subcontractor'}
          </Button>
        </div>
      </form>
    </DetailCard>
  );
}
